import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  IconButton,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip
} from '@mui/material';
import {
  Category as CategoryIcon,
  Image,
  VolumeUp,
  Widgets,
  PhotoLibrary,
  LibraryMusic,
  RecordVoiceOver,
  Add,
  CloudUpload,
  Download,
  MoreVert,
  TrendingUp,
  People,
  FiberManualRecord,
  PhoneAndroid,
  Computer,
  Send as SendIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import categoryService from '../services/categoryService';
import itemService from '../services/itemService';
import { Category } from '../types/category';
import { User } from '../types';
import { ConnectedUserInfo } from '../hooks/usePresence';
import { useUserRole } from '../hooks/useUserRole';

interface AdminDashboardProps {
  user: User | null;
  onNavigateToTab?: (tabIndex: number) => void;
  connectedUsers: ConnectedUserInfo[];
  isConnected: boolean;
  presenceError: string | null;
  sendMessage: (targetUserId: string, message: string) => Promise<void>;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ user, onNavigateToTab, connectedUsers, isConnected, presenceError, sendMessage }) => {
  const { user: currentUser } = useUserRole();
  const isSuperAdmin = currentUser?.roleId === 1;

  // Compose-message dialog state
  const [composeTarget, setComposeTarget] = useState<ConnectedUserInfo | null>(null);
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);

  const handleOpenCompose = (target: ConnectedUserInfo) => {
    setComposeTarget(target);
    setComposeText('');
  };

  const handleSend = async () => {
    if (!composeTarget || !composeText.trim()) return;
    setSending(true);
    try {
      // Send by userId so the message reaches all devices (web + mobile)
      await sendMessage(composeTarget.userId, composeText.trim());
      setComposeTarget(null);
      setComposeText('');
    } catch (err: any) {
      console.error('[Dashboard] sendMessage error', err);
    } finally {
      setSending(false);
    }
  };

  const [stats, setStats] = useState([
    {
      title: 'Total Categories',
      value: '0',
      change: 'Loading...',
      color: 'primary',
      icon: <CategoryIcon />,
      trend: 'up'
    },
    {
      title: 'Total Items',
      value: '0',
      change: 'Loading...',
      color: 'secondary',
      icon: <Widgets />,
      trend: 'up'
    },
    {
      title: 'Categories with Images',
      value: '0',
      change: 'Loading...',
      color: 'info',
      icon: <PhotoLibrary />,
      trend: 'up'
    },
    {
      title: 'Categories with Audio',
      value: '0',
      change: 'Loading...',
      color: 'warning',
      icon: <LibraryMusic />,
      trend: 'up'
    },
    {
      title: 'Items with Images',
      value: '0',
      change: 'Loading...',
      color: 'success',
      icon: <Image />,
      trend: 'up'
    },
    {
      title: 'Items with Audio',
      value: '0',
      change: 'Loading...',
      color: 'error',
      icon: <RecordVoiceOver />,
      trend: 'up'
    }
  ]);

  // Load real category and item statistics
  useEffect(() => {
    const loadStats = async () => {
      try {
        const [categories, items] = await Promise.all([
          categoryService.getCategories(),
          itemService.getItems()
        ]);
        
        const totalCategories = categories.length;
        const totalItems = items.length;
        const categoriesWithImages = categories.filter((category: Category) => category.imageGenerated || category.imageData).length;
        const categoriesWithAudio = categories.filter((category: Category) => 
          category.soundOptions && category.soundOptions.length > 0 && category.soundOptions.some(option => option.soundGenerated || option.soundData)
        ).length;
        const itemsWithImages = items.filter((item: any) => item.imageGenerated || item.imageData).length;
        const itemsWithAudio = items.filter((item: any) => 
          item.soundOptions && item.soundOptions.length > 0 && item.soundOptions.some((option: any) => option.soundGenerated || option.soundData)
        ).length;

        setStats([
          {
            title: 'Total Categories',
            value: totalCategories.toString(),
            change: `${totalCategories} total`,
            color: 'primary',
            icon: <CategoryIcon />,
            trend: 'up'
          },
          {
            title: 'Total Items',
            value: totalItems.toString(),
            change: `${totalItems} total`,
            color: 'secondary',
            icon: <Widgets />,
            trend: 'up'
          },
          {
            title: 'Categories with Images',
            value: categoriesWithImages.toString(),
            change: `${Math.round(categoriesWithImages / totalCategories * 100)}% coverage`,
            color: 'info',
            icon: <PhotoLibrary />,
            trend: 'up'
          },
          {
            title: 'Categories with Audio',
            value: categoriesWithAudio.toString(),
            change: `${Math.round(categoriesWithAudio / totalCategories * 100)}% coverage`,
            color: 'warning',
            icon: <LibraryMusic />,
            trend: 'up'
          },
          {
            title: 'Items with Images',
            value: itemsWithImages.toString(),
            change: `${Math.round(itemsWithImages / totalItems * 100)}% coverage`,
            color: 'success',
            icon: <Image />,
            trend: 'up'
          },
          {
            title: 'Items with Audio',
            value: itemsWithAudio.toString(),
            change: `${Math.round(itemsWithAudio / totalItems * 100)}% coverage`,
            color: 'error',
            icon: <RecordVoiceOver />,
            trend: 'up'
          }
        ]);
      } catch (error) {
        console.error('Error loading stats:', error);
        console.error('GraphQL endpoint:', process.env.REACT_APP_GRAPHQL_ENDPOINT);
        
        // Update stats to show error state
        setStats([
          {
            title: 'Total Categories',
            value: '?',
            change: 'Connection Error',
            color: 'error',
            icon: <CategoryIcon />,
            trend: 'down'
          },
          {
            title: 'Total Items',
            value: '?',
            change: 'Connection Error',
            color: 'error',
            icon: <Widgets />,
            trend: 'down'
          },
          {
            title: 'Items with Images',
            value: '?',
            change: 'Connection Error',
            color: 'error',
            icon: <Image />,
            trend: 'down'
          },
          {
            title: 'Items with Audio',
            value: '?',
            change: 'Connection Error',
            color: 'error',
            icon: <RecordVoiceOver />,
            trend: 'down'
          }
        ]);
      }
    };

    loadStats();
  }, []);

  const quickActions = [
    {
      title: 'Add Category',
      description: 'Create a new category for organizing items',
      icon: <Add />,
      color: 'primary',
      action: () => onNavigateToTab?.(1) // Navigate to Categories tab (index 1)
    },
    {
      title: 'Add Item',
      description: 'Create a new communication item with images and audio',
      icon: <Image />,
      color: 'secondary',
      action: () => onNavigateToTab?.(2) // Navigate to Items tab (index 2)
    },
    {
      title: 'Upload Images',
      description: 'Upload new images for communication items',
      icon: <CloudUpload />,
      color: 'info',
      action: () => onNavigateToTab?.(2) // Navigate to Items tab for image management
    },
    {
      title: 'Manage Audio',
      description: 'Add or edit audio files for items',
      icon: <VolumeUp />,
      color: 'success',
      action: () => onNavigateToTab?.(3) // Navigate to Audio tab (index 3)
    },
    {
      title: 'Export Data',
      description: 'Export all data for backup or analysis',
      icon: <Download />,
      color: 'warning',
      action: () => alert('Export functionality coming soon!') // Placeholder for future feature
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Welcome Message - Simple and Integrated */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800, 
            mb: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '1.75rem', md: '2.125rem' }
          }}
        >
          Welcome back, {user?.displayName || user?.emailAddress || 'Control Panel User'}! 👋
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ 
            mb: 3, 
            fontWeight: 400,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          Here's what's happening with your TalkForTy system today
        </Typography>

        {/* Stats + Quick Actions side by side */}
        <Grid container spacing={4} sx={{ mb: 4 }} alignItems="flex-start">
          {/* Stats Overview — 2/3 width */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={6} key={index}>
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    borderColor: `${stat.color}.light`
                  }
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: `${stat.color}.main`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    color: 'white',
                    boxShadow: 3
                  }}
                >
                  {stat.icon}
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 900, 
                    mb: 0.5,
                    color: 'text.primary',
                    fontSize: { xs: '1.5rem', md: '2rem' }
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 1,
                    fontSize: '0.875rem'
                  }}
                >
                  {stat.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'success.lighter',
                      color: 'success.dark',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    <TrendingUp sx={{ fontSize: 14, mr: 0.5 }} />
                    {stat.change}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
            </Grid>
          </Grid>{/* end stats md=8 */}

          {/* Quick Actions — 1/3 width */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={1}
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 24,
                    backgroundColor: 'primary.main',
                    borderRadius: 1,
                    mr: 2
                  }}
                />
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {quickActions.map((action, index) => (
                  <Box
                    key={index}
                    onClick={action.action}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.25,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        borderColor: `${action.color}.main`,
                        backgroundColor: `${action.color}.50`,
                        pl: 2.5,
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        bgcolor: `${action.color}.main`,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        '& svg': { fontSize: 16 }
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {action.title}
                    </Typography>
                  </Box>
                ))}
              </Box>

            </Paper>
          </Grid>

        </Grid>{/* end outer stats+actions grid */}
      </Box>{/* end Welcome+stats Box */}

        {/* Online Users Panel */}
        <Box sx={{ mt: 4 }}>
          <Paper
            elevation={1}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
              <Badge badgeContent={connectedUsers.length} color="success" max={999}>
                <People />
              </Badge>
              <Typography variant="h6" sx={{ fontWeight: 700, ml: 1 }}>Online Users</Typography>
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: 10 }} />}
                label={isConnected ? 'Live' : 'Connecting…'}
                size="small"
                color={isConnected ? 'success' : 'warning'}
                variant="outlined"
                sx={{ ml: 'auto' }}
              />
            </Box>

            {presenceError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                ⚠ {presenceError}
              </Typography>
            )}

            {!isSuperAdmin ? (
              <Typography variant="body2" color="text.secondary">
                Super Admin access required to view the live user list.
              </Typography>
            ) : connectedUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {isConnected ? 'No users currently online.' : 'Waiting for connection…'}
              </Typography>
            ) : (
              <List dense disablePadding>
                {connectedUsers.map((u) => (
                  <ListItem key={u.connectionId} disablePadding sx={{ py: 0.5 }}
                    secondaryAction={
                      isSuperAdmin ? (
                        <Tooltip title={`Send message to ${u.displayName || u.username}`}>
                          <IconButton size="small" onClick={() => handleOpenCompose(u)} sx={{ color: 'primary.main' }}>
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: u.roleId === 1 ? '#6366f1' : '#0078d4' }}>
                        {(u.displayName || u.username || '?')[0].toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {u.displayName || u.username}
                          </Typography>
                          {u.roleId === 1 && (
                            <Chip label="Super Admin" size="small" color="secondary" sx={{ height: 16, fontSize: 10 }} />
                          )}
                          {u.roleId === 2 && (
                            <Chip label="Admin" size="small" color="primary" sx={{ height: 16, fontSize: 10 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {u.clientType === 'mobile' ? (
                            <PhoneAndroid sx={{ fontSize: 12 }} />
                          ) : (
                            <Computer sx={{ fontSize: 12 }} />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {u.emailAddress} · connected {new Date(u.connectedAt).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Box>

        {/* Compose message dialog */}
        <Dialog open={Boolean(composeTarget)} onClose={() => setComposeTarget(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChatIcon color="primary" />
            Send Message
          </DialogTitle>
          <DialogContent>
            {composeTarget && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  To:{' '}
                  <strong>{composeTarget.displayName || composeTarget.username}</strong>
                  {' '}({composeTarget.emailAddress})
                  {' '}
                  <Chip
                    icon={composeTarget.clientType === 'mobile' ? <PhoneAndroid sx={{ fontSize: 12 }} /> : <Computer sx={{ fontSize: 12 }} />}
                    label={composeTarget.clientType}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 0.5 }}
                  />
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              label="Message"
              placeholder="Type your message here…"
              value={composeText}
              onChange={e => setComposeText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSend(); }}
              inputProps={{ maxLength: 2000 }}
              helperText={`${composeText.length}/2000 · Ctrl+Enter to send`}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setComposeTarget(null)} disabled={sending}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={!composeText.trim() || sending}
              onClick={handleSend}
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
  );
};
